import * as React from 'react';

import { isDevelopmentBuild } from '@/env';

const MANIFEST_HREF = '/app/manifest.webmanifest';
const SERVICE_WORKER_URL = '/app/service-worker.js';
const SERVICE_WORKER_SCOPE = '/app/';

/**
 * Makes the member app installable, and only the member app.
 *
 * The site is one bundle serving two audiences from one HTML document, so the manifest
 * link is attached while a member is inside `/app` and removed when they leave. Without
 * that, a visitor reading the marketing pages would be offered "install H2HSpark" for an
 * app they have no account on.
 *
 * The service worker is registered from a script that already sits under `/app/`, which is
 * what limits its scope to the member app — the marketing pages stay uncontrolled and keep
 * serving fresh from the network.
 */
export function useMemberAppInstallability(): void {
  React.useEffect(() => {
    const existing = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (existing !== null) return undefined;

    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = MANIFEST_HREF;
    document.head.appendChild(link);

    return () => {
      link.remove();
    };
  }, []);

  React.useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    // Registering during development would serve a cached shell over the dev server's
    // hot-reloading one, which looks exactly like "my edit didn't apply".
    if (isDevelopmentBuild()) return;

    void navigator.serviceWorker
      .register(SERVICE_WORKER_URL, { scope: SERVICE_WORKER_SCOPE })
      .catch(() => {
        // Offline support is an enhancement. A registration failure — private browsing,
        // an unsupported browser, a blocked script — leaves a fully working online app.
      });
  }, []);
}
